/**
 * `<StatusPill>` — single source of truth for candidate status display.
 *
 * Phase 1 (post-walkthrough) refactor:
 *   - New props: `variant: "tenant" | "pipeline" | "upload"`, `value: string`, `size: "sm" | "md"`.
 *   - Label resolution is component-owned — consumers pass `value`, never a label string.
 *   - Leading `+` replaced with a designed dot `<span className="h-1.5 w-1.5 rounded-full bg-current">`.
 *   - Deprecated `status` prop aliased to `value` for backward compat during Phase 2 sweep.
 *
 * Phase 2.4.1 — `variant="upload"` carries the simplified candidate-add
 * lifecycle badge set: receiving / uploading / ranking → in-process;
 * complete → green; rank_one_failed → amber-alert; manual → active.
 * The Phase 2.4 parse/eval states (parsing, evaluating, eval_failed) are
 * removed — rank-one is the only AI step in 2.4.1.
 *
 * Phase 2.5 — `variant="tenant"` now also handles the new DB-backed
 * `candidates.tenant_status` enum values: `available | hired | rejected |
 * opted_out | unavailable`.  The legacy pipeline-stage values (`active`,
 * `phone_screen`, etc.) still fall-through for backward compat.
 * `TenantStatus` is the Phase 2.5 canonical type; consumers on the new
 * endpoints should import it from `@/lib/candidatesApi`.
 *
 * Spec: design/post-walkthrough/global-patterns-spec.md §4.3.
 *
 * Sizes:
 *   sm — h-5 px-2 text-[10px]
 *   md — h-6 px-2.5 text-xs  (DEFAULT)
 *
 * Color: semantic status tokens via bg-status-*-bg / text-status-*-fg / ring-status-*-ring
 * keys from tailwind.config.ts → tokens.css --status-* vars.
 *
 * Note: dimmed treatment is applied at the parent card wrapper level, not
 * on the pill itself (double-dimming makes pills unreadable — see prior comment).
 */

/** Backend-native pipeline-stage status values (DO NOT change — matches DB enum). */
export type CandidateStatus =
  | "active"
  | "phone_screen"
  | "interview_scheduled"
  | "interviewed"
  | "offer"
  | "hired"
  | "rejected"
  | "opted_out";

/**
 * Upload-lifecycle badge values (Phase 2.4.1 simplified set).
 * Lifecycle: receiving → uploading → ranking → complete | rank_one_failed | manual.
 * Internal — consumers pass the string value directly via the `value` prop.
 */
type UploadBadgeValue =
  | "receiving"
  | "uploading"
  | "ranking"
  | "complete"
  | "rank_one_failed"
  | "manual";

interface StatusPillProps {
  /** Determines which label/palette map to use. */
  variant: "tenant" | "pipeline" | "upload";
  /** Backend status value. Component resolves display label. */
  value: CandidateStatus | UploadBadgeValue | string;
  /**
   * Override the computed display label. Use sparingly — component labels
   * are the canonical microcopy. Only for tenant-customized pipeline stages.
   */
  label?: string;
  /** Visual size. Default: "md". */
  size?: "sm" | "md";
  /**
   * @deprecated Use `variant` + `value` instead.
   * Kept for Phase 2 call-site sweep. Aliased to `value`.
   */
  status?: CandidateStatus;
  /** @deprecated Reserved — dimmed treatment is applied at card wrapper level. */
  dimmed?: boolean;
}

// ── Tenant variant ─────────────────────────────────────────────────────────

const TENANT_LABELS: Record<string, string> = {
  // Phase 2.5 canonical tenant_status enum values (candidates.tenant_status column).
  available:            "Available",
  unavailable:          "Unavailable",
  hired:                "Hired",
  rejected:             "Rejected",
  opted_out:            "Opted Out",
  // Legacy pipeline-stage fallbacks for backward compat.
  active:               "Available",    // backend "active" = tenant "available"
  phone_screen:         "Unavailable",  // pipeline-stage value in tenant context
  interview_scheduled:  "Unavailable",  // same
  interviewed:          "Unavailable",  // same
  offer:                "Hired",        // offer stage — closest tenant analog
};

const TENANT_PALETTE: Record<string, string> = {
  // Phase 2.5 canonical tenant_status enum values.
  available:            "bg-status-active-bg text-status-active-fg ring-status-active-ring",
  unavailable:          "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  // Legacy pipeline-stage fallbacks.
  active:               "bg-status-active-bg text-status-active-fg ring-status-active-ring",
  hired:                "bg-status-hired-bg text-status-hired-fg ring-status-hired-ring",
  rejected:             "bg-status-rejected-bg text-status-rejected-fg ring-status-rejected-ring",
  opted_out:            "bg-status-opted-out-bg text-status-opted-out-fg ring-status-opted-out-ring",
  phone_screen:         "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  interview_scheduled:  "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  interviewed:          "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  offer:                "bg-status-hired-bg text-status-hired-fg ring-status-hired-ring",
};

// ── Pipeline variant ───────────────────────────────────────────────────────

const PIPELINE_LABELS: Record<string, string> = {
  active:               "Active",
  phone_screen:         "Phone Screen",
  interview_scheduled:  "Interview Scheduled",
  interviewed:          "Interviewed",
  offer:                "Offer Extended",   // backend "offer" = pipeline "offer_extended"
  offer_extended:       "Offer Extended",   // future-proofed if backend migrates
  hired:                "Hired",
  rejected:             "Rejected",
  opted_out:            "Opted Out",
  // JD lifecycle (jd-rebuild-spec.md §2.1) — pipeline variant carries
  // job-status pills as well as candidate-stage pills. Tokens chosen per
  // spec: draft → in-process (amber), paused → opted-out (slate),
  // archived → rejected (red, low-saturation).
  draft:                "Draft",
  paused:               "Paused",
  archived:             "Archived",
};

const PIPELINE_PALETTE: Record<string, string> = {
  active:               "bg-status-active-bg text-status-active-fg ring-status-active-ring",
  phone_screen:         "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  interview_scheduled:  "bg-status-scheduled-bg text-status-scheduled-fg ring-status-scheduled-ring",
  interviewed:          "bg-status-interviewed-bg text-status-interviewed-fg ring-status-interviewed-ring",
  offer:                "bg-status-offer-bg text-status-offer-fg ring-status-offer-ring",
  offer_extended:       "bg-status-offer-bg text-status-offer-fg ring-status-offer-ring",
  hired:                "bg-status-hired-bg text-status-hired-fg ring-status-hired-ring",
  rejected:             "bg-status-rejected-bg text-status-rejected-fg ring-status-rejected-ring",
  opted_out:            "bg-status-opted-out-bg text-status-opted-out-fg ring-status-opted-out-ring",
  // JD lifecycle palette (see comment above).
  draft:                "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  paused:               "bg-status-opted-out-bg text-status-opted-out-fg ring-status-opted-out-ring",
  archived:             "bg-status-rejected-bg text-status-rejected-fg ring-status-rejected-ring",
};

// ── Upload variant ─────────────────────────────────────────────────────────
//
// Phase 2.4.1 simplified lifecycle: receiving → uploading → ranking →
// complete | rank_one_failed | manual.
// `uploading` carries an interpolated percent label via the `label` prop
// override on the consumer side (e.g. `label="Uploading 42%"`).

const UPLOAD_LABELS: Record<string, string> = {
  receiving:        "Receiving…",
  uploading:        "Uploading…",
  ranking:          "Ranking…",
  complete:         "Done",
  rank_one_failed:  "Ranking failed",
  manual:           "Saved manually",
};

const UPLOAD_PALETTE: Record<string, string> = {
  receiving:
    "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  uploading:
    "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  ranking:
    "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  complete:
    "bg-status-offer-bg text-status-offer-fg ring-status-offer-ring",
  rank_one_failed:
    "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring",
  manual:
    "bg-status-active-bg text-status-active-fg ring-status-active-ring",
};

/**
 * Which upload badges show a spinner, a check, or an alert glyph.
 * Drives the optional leading icon on the upload variant; tenant/pipeline
 * variants keep the simple dot.
 */
const UPLOAD_GLYPH: Record<string, "spinner" | "check" | "alert"> = {
  receiving:        "spinner",
  uploading:        "spinner",
  ranking:          "spinner",
  complete:         "check",
  rank_one_failed:  "alert",
  manual:           "check",
};

// Fallback palette when value is unknown.
const FALLBACK_PALETTE =
  "bg-status-active-bg text-status-active-fg ring-status-active-ring";

// ── Size classes ───────────────────────────────────────────────────────────

const SIZE_CLASSES: Record<"sm" | "md", string> = {
  sm: "h-5 px-2 text-[10px]",
  md: "h-6 px-2.5 text-xs",
};

// ── Component ─────────────────────────────────────────────────────────────

export function StatusPill({
  variant,
  value,
  label: labelOverride,
  size = "md",
  status,
  // dimmed is a no-op — see component header
}: StatusPillProps): JSX.Element {
  // Phase 2 back-compat: if only `status` is passed (old API), alias to `value`.
  const resolvedValue = value ?? status ?? "active";

  const labelMap =
    variant === "tenant"
      ? TENANT_LABELS
      : variant === "pipeline"
        ? PIPELINE_LABELS
        : UPLOAD_LABELS;
  const paletteMap =
    variant === "tenant"
      ? TENANT_PALETTE
      : variant === "pipeline"
        ? PIPELINE_PALETTE
        : UPLOAD_PALETTE;

  const label = labelOverride ?? labelMap[resolvedValue] ?? resolvedValue;
  const palette = paletteMap[resolvedValue] ?? FALLBACK_PALETTE;
  const sizeClass = SIZE_CLASSES[size];
  const glyph =
    variant === "upload" ? UPLOAD_GLYPH[resolvedValue] ?? "spinner" : null;

  return (
    <span
      data-variant={variant}
      data-status={resolvedValue}
      className={`inline-flex items-center gap-1 rounded-full font-medium uppercase tracking-wide ring-1 ring-inset ${sizeClass} ${palette}`}
    >
      {/* Upload variant: spinner / check / alert glyph. Other variants:
          designed leading dot — replaces any prior leading + character. */}
      {glyph === "spinner" ? (
        <span
          className="inline-block h-2 w-2 shrink-0 animate-spin rounded-full border border-current border-t-transparent"
          role="status"
          aria-hidden="true"
        />
      ) : glyph === "check" ? (
        <svg
          className="h-2.5 w-2.5 shrink-0"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2 6.5l2.5 2.5L10 3.5" />
        </svg>
      ) : glyph === "alert" ? (
        <svg
          className="h-2.5 w-2.5 shrink-0"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="6" cy="6" r="5" />
          <path d="M6 3.5v3" />
          <path d="M6 8.5h.01" />
        </svg>
      ) : (
        <span
          className="h-1.5 w-1.5 rounded-full bg-current"
          aria-hidden="true"
        />
      )}
      {label}
    </span>
  );
}
